import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Calendar, DollarSign, FileText } from 'lucide-react';
import { EventWithTransactions, BillingTransaction } from '../../services/billingService';
import { useAppSelector } from '../../store/hooks';
import TransactionTable from './TransactionTable';
import clsx from 'clsx';

interface EventTransactionCardProps {
  event: EventWithTransactions;
}

const EventTransactionCard: React.FC<EventTransactionCardProps> = ({ event }) => {
  const { theme } = useAppSelector((state) => state.ui);
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string): string => {
    // Time is in HH:MM:SS format, extract HH:MM
    return timeString.substring(0, 5);
  };

  const totalDeductions = parseFloat(event.total_deductions || '0');
  const hasTransactions = event.transaction_count > 0;

  return (
    <div className={`rounded-lg border overflow-hidden transition-all ${
      theme === 'dark' 
        ? 'bg-gray-900/80 border-gray-700/50' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Card Header - Always Visible */}
      <div className={`p-4 transition-colors ${
        theme === 'dark' ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'
      }`}>
        <div 
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center justify-between cursor-pointer"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {event.series_id && !event.is_main_event_in_series && (
                    <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                      theme === 'dark' 
                        ? 'bg-blue-900/30 text-blue-400 border border-blue-700/50' 
                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                    }`}>
                      Sub-event
                    </span>
                  )}
                  <h3 className={`text-lg font-semibold truncate ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {event.title}
                  </h3>
                </div>
                {event.series_id && !event.is_main_event_in_series && event.series_name && (
                  <div className={`text-xs mt-1 ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Part of: {event.series_name}
                  </div>
                )}
              </div>
              <span 
                onClick={(e) => e.stopPropagation()}
                className={`px-2 py-1 text-xs font-medium rounded flex-shrink-0 ${
                  event.status === 'active' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                    : event.status === 'pending'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                {event.status}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <Calendar className="h-4 w-4" />
                <span>{formatDate(event.event_date)}</span>
                {event.event_time && (
                  <span className="ml-1">at {formatTime(event.event_time)}</span>
                )}
              </div>
              {event.location && (
                <div className={`flex items-center gap-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  <FileText className="h-4 w-4" />
                  <span className="truncate max-w-xs">{event.location}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-4 ml-4">
            {/* Transaction Summary */}
            <div 
              onClick={(e) => e.stopPropagation()}
              className="text-right"
            >
              {hasTransactions ? (
                <>
                  <div className={`text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-900'
                  }`}>
                    {event.transaction_count} transaction{event.transaction_count !== 1 ? 's' : ''}
                  </div>
                  {totalDeductions > 0 && (
                    <div className={`text-sm ${
                      theme === 'dark' ? 'text-red-400' : 'text-red-600'
                    }`}>
                      -{totalDeductions.toFixed(2)} JOD
                    </div>
                  )}
                </>
              ) : (
                <div className={`text-sm ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  No transactions
                </div>
              )}
            </div>
            
            {/* Expand/Collapse Icon */}
            <div className="flex-shrink-0">
              {isExpanded ? (
                <ChevronUp className={`h-5 w-5 transition-transform ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`} />
              ) : (
                <ChevronDown className={`h-5 w-5 transition-transform ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content - Transactions */}
      {isExpanded && (
        <div 
          onClick={(e) => e.stopPropagation()}
          className={`border-t ${
            theme === 'dark' ? 'border-gray-700 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          {hasTransactions ? (
            <div className="p-4">
              <TransactionTable
                transactions={event.transactions}
                loading={false}
                showHostColumn={false}
              />
            </div>
          ) : (
            <div className={`p-8 text-center ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`}>
              <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No transactions for this event yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventTransactionCard;

